/**
 * Main Application Component
 *
 * Top-level routing:
 *   /tools/:slug      → SEOToolPage       (public, standalone)
 *   /tools             → ToolsDirectoryPage (public, standalone)
 *   /pipelines/:slug   → SEOPipelinePage   (public, standalone)
 *   everything else    → AppShell          (internal app UI)
 */

import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Route,Routes } from 'react-router-dom';

import { Toaster } from './components/ui/toaster';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useSharedPipeline } from './hooks/useSharedPipeline';
import { AppShell } from './pages/app/AppShell';
import { useAppStore } from './store/useAppStore';
import { usePreferencesStore, useResolvedTheme } from './store/usePreferencesStore';
import { registerAllTools } from './tools/registry';

// Lazy-loaded SEO pages (Kaylee will replace stubs with full implementations)
const ToolsDirectoryPage = React.lazy(() => import('./pages/seo/ToolsDirectoryPage').then(m => ({ default: m.ToolsDirectoryPage })));
const SEOToolPage = React.lazy(() => import('./pages/seo/SEOToolPage').then(m => ({ default: m.SEOToolPage })));
const SEOPipelinePage = React.lazy(() => import('./pages/seo/SEOPipelinePage').then(m => ({ default: m.SEOPipelinePage })));

type SEORoute =
  | { page: 'tools-directory'; slug: null }
  | { page: 'seo-tool'; slug: string }
  | { page: 'seo-pipeline'; slug: string };

function matchSEORoute(pathname: string): SEORoute | null {
  const pathOnly = pathname.split(/[?#]/)[0] ?? pathname;
  const segments = pathOnly.split('/').filter(Boolean);

  if (segments[0] === 'tools' && segments[1]) {
    return { page: 'seo-tool', slug: segments[1] };
  }
  if (segments[0] === 'tools' && !segments[1]) {
    return { page: 'tools-directory', slug: null };
  }
  if (segments[0] === 'pipelines' && segments[1]) {
    return { page: 'seo-pipeline', slug: segments[1] };
  }
  return null;
}

// Register all tools on app load
registerAllTools();

export const App: React.FC = () => {
  const theme = useResolvedTheme();
  const { runPipeline } = useAppStore();
  const toggleSidebar = usePreferencesStore(state => state.toggleSidebar);

  const [pathname, setPathname] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const seoRoute = useMemo(() => matchSEORoute(pathname), [pathname]);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [theme]);

  // Set up keyboard shortcuts
  useKeyboardShortcuts({
    onRunPipeline: runPipeline,
    onToggleSidebar: toggleSidebar,
  });

  // Load shared pipeline from URL hash (runs after tool registration)
  useSharedPipeline();

  // SEO routes render standalone pages (no app shell chrome)
  if (seoRoute) {
    return (
      <BrowserRouter>
        <Suspense fallback={<div className="p-8 text-center">Loading…</div>}>
          <Routes>
            <Route path="/tools" element={<ToolsDirectoryPage />} />
            <Route path="/tools/:slug" element={<SEOToolPage />} />
            <Route path="/pipelines/:slug" element={<SEOPipelinePage />} />
          </Routes>
        </Suspense>
        <Toaster />
      </BrowserRouter>
    );
  }

  return (
    <>
      <AppShell />
      <Toaster />
    </>
  );
};



